import React from 'react';

import { LinearGaugeAdditionalArrowsDataType } from '../../../components/UI-Elements/Charts/LinearGauge/LinearGauge';
import { IRiskTolerance } from '../../../sp-types/accountTypes';
import { IBalanceFlow, balanceMethodType } from '../../../sp-types/balanceFlowType';
import { FormatToPriceTemplate } from '../../../utils/FormatValue';

export const BALANCE_METHOD_TO_IMAGE = {
  'full-protection': {
    arrowImage: '/assets/icons/arrows/1-arrow.svg',
    roundImage: '/assets/icons/arrows/1-circle.svg',
  },
  'good-protection': {
    arrowImage: '/assets/icons/arrows/2-arrow.svg',
    roundImage: '/assets/icons/arrows/2-circle.svg',
  },
  'risky-protection': {
    arrowImage: '/assets/icons/arrows/3-arrow.svg',
    roundImage: '/assets/icons/arrows/3-circle.svg',
  },
};

export interface IBalancingMethod {
  title?: string;
  exposure: number;
  infoTexts: { title: string; data: string | React.ReactNode }[];
  type: balanceMethodType;
  isSelected?: boolean;
  onClick?: (_balanceType: string) => void;
}

export const mapAdditionalArrowsByBalancingMethod = (
  balancingMethodsData: IBalancingMethod[],
  selectedBalanceMethod?: string,
): LinearGaugeAdditionalArrowsDataType[] => {
  return balancingMethodsData.map((item) => ({
    value: item.exposure,
    img: BALANCE_METHOD_TO_IMAGE[item.type].arrowImage,
    bigArrow: selectedBalanceMethod === item.type ? true : false,
  }));
};

// Todo ReWrite
// Written Not Good - Need to be rewritten -- Will Happen when will use all the data
export const balanceFlowToOptionalMethods = (
  currentExposure: number,
  riskTolerance: IRiskTolerance,
  selectedProtectionType: string,
  expectedPrice: number,
  expectedDateRange: string,
  setSelectedProtection: (_value: string) => void,
  materialPrice: number,
): IBalancingMethod[] => {
  const balanceFlow: IBalancingMethod[] = [];
  const infoTextsTemplate = [
    {
      title: 'Expected price',
      data: (
        <span className={expectedPrice - materialPrice < 0 ? 'text-red' : 'text-green'}>
          ${(expectedPrice - materialPrice).toFixed(0)}{' '}
        </span>
      ),
    },
    { title: 'Due dates', data: expectedDateRange },
  ];

  const createBalancingMethod = (exposure: number, type: balanceMethodType, difference: number): IBalancingMethod => ({
    exposure,
    type: type,
    title: '',
    infoTexts: [
      ...infoTextsTemplate,
      {
        title: 'Hedge quantity',
        data: Math.abs(Number(difference / materialPrice)).toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }), // TODO: add units from materialsMap
      },
      { title: 'Material price', data: FormatToPriceTemplate(materialPrice) },
      { title: 'Total', data: FormatToPriceTemplate(difference.toFixed(0)) },
    ],
    isSelected: selectedProtectionType === type,
    onClick: setSelectedProtection,
  });

  if (currentExposure > riskTolerance.warnPlus) {
    balanceFlow.push(
      createBalancingMethod(
        riskTolerance.warnPlus,
        'risky-protection',
        Math.abs(currentExposure - riskTolerance.warnPlus),
      ),
    );
  }

  if (currentExposure > riskTolerance.warnPlus) {
    balanceFlow.push(
      createBalancingMethod(
        riskTolerance.warnPlus,
        'good-protection',
        Math.abs(currentExposure - riskTolerance.warnPlus),
      ),
    );
  }

  if (currentExposure < riskTolerance.warnMinus) {
    balanceFlow.push(
      createBalancingMethod(
        riskTolerance.warnMinus,
        'good-protection',
        Math.abs(currentExposure - riskTolerance.warnMinus),
      ),
    );
  }

  if (currentExposure < riskTolerance.warnMinus) {
    balanceFlow.push(
      createBalancingMethod(
        riskTolerance.warnMinus,
        'risky-protection',
        Math.abs(currentExposure - riskTolerance.warnMinus),
      ),
    );
  }

  const stableAreaMidPoint = (riskTolerance.warnMinus + riskTolerance.warnPlus) / 2;
  balanceFlow.push(
    createBalancingMethod(stableAreaMidPoint, 'full-protection', Math.abs(currentExposure - stableAreaMidPoint)),
  );

  return balanceFlow;
};

export const getBalanceMethodExposureLocation = (
  balanceType: balanceMethodType,
  riskTolerance: IRiskTolerance,
  currentBalanceExposure: number,
): number => {
  const zeroPoint = (riskTolerance.warnMinus + riskTolerance.warnPlus) / 2;
  if (balanceType === 'full-protection') {
    return zeroPoint;
  }
  if (balanceType === 'good-protection' && currentBalanceExposure < zeroPoint) return riskTolerance.warnMinus;
  if (balanceType === 'good-protection' && currentBalanceExposure > zeroPoint) return riskTolerance.warnPlus;
  if (balanceType === 'risky-protection' && currentBalanceExposure > zeroPoint)
    return riskTolerance.warnPlus;
  if (balanceType === 'risky-protection' && currentBalanceExposure < zeroPoint)
    return riskTolerance.warnMinus;

  return 0;
};

//Todo - ReWrite this with logic
export const getBalanceExpectedDateRange = (balanceFlow: IBalanceFlow): string => {
  const startDate = balanceFlow.startDate.toDate().toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  const endDate = balanceFlow.dueDate.toDate().toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  return startDate + ' - ' + endDate;
};
